import React, { useState } from 'react';
import styled from 'styled-components';
import { Mail, Eye, EyeOff, ArrowLeft, User } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthService } from '../firebase';

const PageContainer = styled.div`
  padding-top: var(--header-h);
  min-height: 100vh;
  background: white;
  width: 100%;
  display: flex;
`;

const LeftSide = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const VideoBackground = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
`;

const VideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.1);
  z-index: 1;
`;

const RightSide = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
`;

const Container = styled.div`
  max-width: 400px;
  width: 100%;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #333;
  font-size: 16px;
  cursor: pointer;
  margin-bottom: 30px;
  transition: color 0.3s ease;

  &:hover {
    color: #666;
  }
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 3px;
  padding: 44px 40px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.08);
  border: 1px solid #e8e8e8;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;

  h1 {
    font-size: 2rem;
    color: #333;
    margin-bottom: 10px;
    font-weight: 300;
  }

  p {
    font-size: 1rem;
    color: #666;
    margin: 0;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const InputContainer = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 15px 20px;
  padding-right: 50px;
  border: 2px solid #e0e0e0;
  border-radius: 3px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: #333;
  }

  &::placeholder {
    color: #999;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 5px;

  &:hover {
    color: #333;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 18px 30px;
  background: #1a1a1a;
  color: white;
  border: 1px solid #1a1a1a;
  border-radius: 2px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: background 0.3s ease;

  &:hover {
    background: #555;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 30px 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e0e0e0;
  }
  
  span {
    padding: 0 20px;
    color: #999;
    font-size: 14px;
  }
`;

const LinkText = styled.div`
  text-align: center;
  margin-top: 30px;
  
  p {
    color: #666;
    margin: 0;
  }
  
  a {
    color: #333;
    text-decoration: none;
    font-weight: 600;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #c62828;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 20px;
  border-left: 4px solid #c62828;
`;

const ForgotRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: -8px 0 4px;

  button {
    background: none;
    border: none;
    padding: 0;
    font-size: 0.85rem;
    color: #555;
    cursor: pointer;
    text-decoration: underline;
    font-family: inherit;

    &:hover:not(:disabled) {
      color: #1a1a1a;
    }

    &:disabled {
      color: #aaa;
      cursor: not-allowed;
    }
  }
`;

const ResetNotice = styled.div`
  background: #f4f7f4;
  border-left: 3px solid #1a7f3c;
  padding: 12px 14px;
  font-size: 0.85rem;
  color: #2f5d3f;
  line-height: 1.5;
`;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [attempts, setAttempts] = useState(0);
  const [lockUntil, setLockUntil] = useState<number | null>(null);
  const [resetting, setResetting] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  // Basarisiz giris denemelerini sinirlar.
  // Amac: sifre deneme (brute force) saldirilarini yavaslatmak. Bu kontrol
  // tarayicida oldugu icin tek basina yeterli degildir - Firebase Auth kendi
  // tarafinda da hiz siniri uygular - ama kolay otomasyonu engeller.
  const MAX_ATTEMPTS = 5;
  const LOCK_SECONDS = 60;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (lockUntil && Date.now() < lockUntil) {
      const kalan = Math.ceil((lockUntil - Date.now()) / 1000);
      setError(`Çok fazla başarısız deneme. Lütfen ${kalan} saniye sonra tekrar deneyin.`);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const email = formData.email.trim().toLowerCase();
      const result = await AuthService.signIn(email, formData.password);

      if (result.success) {
        setAttempts(0);
        setLockUntil(null);
        navigate('/');
        return;
      }

      // Hata mesaji bilerek genel tutuluyor.
      // Firebase "kullanici bulunamadi" ile "sifre hatali" ayrimini yapar;
      // bunu kullaniciya yansitmak, bir e-postanin sistemde kayitli olup
      // olmadigini disariya sizdirir (hesap sayimi zafiyeti).
      const next = attempts + 1;
      setAttempts(next);

      if (next >= MAX_ATTEMPTS) {
        setLockUntil(Date.now() + LOCK_SECONDS * 1000);
        setAttempts(0);
        setError(`Çok fazla başarısız deneme. ${LOCK_SECONDS} saniye bekleyin.`);
      } else {
        setError('E-posta veya şifre hatalı.');
      }
    } catch (err) {
      setError('Giriş yapılırken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsLoading(false);
    }
  };

  // --- Sifre sifirlama ---
  const handleForgotPassword = async () => {
    const email = formData.email.trim().toLowerCase();

    if (!email) {
      setError('Şifre sıfırlama bağlantısı için önce e-posta adresinizi girin.');
      return;
    }

    setResetting(true);
    setError('');
    await AuthService.sendPasswordResetEmail(email);
    setResetting(false);

    // Sonuc her durumda ayni: adres kayitli olmasa bile "gonderildi" denir,
    // aksi halde hangi adreslerin kayitli oldugu ogrenilebilirdi.
    setResetSent(true);
  };

  return (
    <PageContainer>
      <LeftSide>
        <VideoBackground
          autoPlay
          loop
          muted
          playsInline
        >
          <source src="/pictures/login.mp4" type="video/mp4" />
        </VideoBackground>
        <VideoOverlay />
      </LeftSide>
      <RightSide>
        <Container>
          <BackButton onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
            Geri Dön
          </BackButton>

          <LoginCard>
            <Header>
              <h1>Giriş Yap</h1>
              <p>Hesabınıza giriş yapın</p>
            </Header>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>E-posta</Label>
                <InputContainer>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="ornek@email.com"
                    autoComplete="email"
                    required
                  />
                  <InputIcon>
                    <Mail size={20} />
                  </InputIcon>
                </InputContainer>
              </FormGroup>

              <FormGroup>
                <Label>Şifre</Label>
                <InputContainer>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Şifrenizi girin"
                    autoComplete="current-password"
                    required
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </PasswordToggle>
                </InputContainer>
              </FormGroup>

              <ForgotRow>
                <button type="button" onClick={handleForgotPassword} disabled={resetting}>
                  {resetting ? 'Gönderiliyor...' : 'Şifremi unuttum'}
                </button>
              </ForgotRow>

              {resetSent && (
                <ResetNotice>
                  Girdiğiniz adres sistemimizde kayıtlıysa şifre sıfırlama bağlantısı
                  gönderildi. Lütfen e-postanızı (ve spam klasörünü) kontrol edin.
                </ResetNotice>
              )}

              <SubmitButton type="submit" disabled={isLoading}>
                <User size={20} />
                {isLoading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
              </SubmitButton>
            </Form>

            <Divider>
              <span>veya</span>
            </Divider>

            <LinkText>
              <p>Hesabınız yok mu? <Link to="/register">Kayıt olun</Link></p>
            </LinkText>
          </LoginCard>
        </Container>
      </RightSide>
    </PageContainer>
  );
};

export default LoginPage;
